import { MutableRefObject, useCallback, useEffect, useState } from 'react';

import { CommonInputTextProps } from '#/components/Form/FormInput/FormInput.types';
import { noop } from '#/service/utils';

import { setInitialState, toISOString } from './utils/calculations';

interface InternalFormState {
  [key: string]: string;
}

interface UseInputTimestampProps
  extends Pick<
    CommonInputTextProps,
    | 'dataKey'
    | 'defaultValue'
    | 'handleValidate'
    | 'groupKey'
    | 'updateInputValue'
    | 'displayConfiguration'
    | 'validations'
  > {}

export const useInputTimestamp = ({
  dataKey,
  defaultValue,
  handleValidate = noop,
  groupKey = '',
  updateInputValue = noop,
  displayConfiguration,
}: UseInputTimestampProps) => {
  const [internalFormState, setInternalFormState] = useState<InternalFormState>(
    () => {
      // defaultValue is common between all form inputs, so it can be many types.
      // but we only support strings here, so we can ignore any other type.
      const stringValue =
        typeof defaultValue === 'string' ? defaultValue : undefined;

      return setInitialState({ defaultValue: stringValue, dataKey });
    }
  );

  const [touched, setTouched] = useState(false);

  const dateValue = internalFormState[`${dataKey}_date`];
  const timeValue = internalFormState[`${dataKey}_time`];

  const updateInternalState = useCallback((dataKey: string, value: string) => {
    setInternalFormState((prevState) => ({
      ...prevState,
      [dataKey]: value,
    }));
  }, []);

  const validate = useCallback(
    (inputRef?: MutableRefObject<HTMLInputElement | null>, value?: string) => {
      handleValidate(dataKey, groupKey, inputRef, value, displayConfiguration);
    },
    [dataKey, displayConfiguration, groupKey, handleValidate]
  );

  useEffect(() => {
    updateInputValue(dataKey, { touched });
  }, [dataKey, touched, updateInputValue]);

  useEffect(() => {
    if (dateValue && timeValue) {
      updateInputValue(dataKey, { value: toISOString(dateValue, timeValue) });
    }
  }, [dataKey, dateValue, timeValue, updateInputValue]);

  useEffect(() => {
    if (dateValue && timeValue) {
      validate(undefined, toISOString(dateValue, timeValue));
    }
  }, [validate, dateValue, timeValue]);

  return {
    internalFormState,
    updateInternalState,
    validate,
    setTouched,
    touched,
  };
};
